/**
 * Mermaid Diagram Block Renderer
 *
 * Renders the `ui_mermaid_diagram` tool block. The server validator has
 * already parsed the source, and a rejected attempt makes the model retry —
 * so a source that fails here renders nothing (`onInvalid="hide"`): the
 * failed attempt stays out of the transcript live and on reload alike, and
 * the retried block beside it carries the diagram.
 */

import { MermaidDiagram, mermaidDiagramKind } from "@strivelabs/ui-blocks";
import type { UIBlock } from "@/types/ui-blocks";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { useTrack } from "@/lib/analytics/useTrack";

export interface MermaidDiagramBlockRendererProps {
  block: UIBlock;
}

export function MermaidDiagramBlockRenderer({ block }: MermaidDiagramBlockRendererProps) {
  const trackEvent = useTrack();
  if (block.type !== "mermaid_diagram") return null;

  const { source, title, caption } = block.content;
  if (typeof source !== "string" || !source.trim()) return null;

  const kind = mermaidDiagramKind(source);

  return (
    <MermaidDiagram
      source={source}
      title={title ?? undefined}
      caption={caption ?? undefined}
      onInvalid="hide"
      onRenderError={() => {
        trackEvent(ANALYTICS_EVENTS.CHAT_DIAGRAM_RENDER_FAILED, { via: "tool", kind });
      }}
      onAction={(action) => {
        trackEvent(ANALYTICS_EVENTS.CHAT_DIAGRAM_USED, { action, via: "tool", kind });
      }}
    />
  );
}
